import type { ToastItem, ToastType } from '@/contexts/ToastContext'
import styles from './Toast.module.css'

const TITLES: Record<ToastType, string> = {
  success: 'Feito!',
  error: 'Algo correu mal',
  warning: 'Atenção',
  info: 'Aviso',
}

function ToastIcon({ type }: { type: ToastType }) {
  const common = { className: styles.svgIcon, viewBox: '0 0 24 24', fill: 'none', 'aria-hidden': true as const }
  if (type === 'success') {
    return (
      <svg {...common}>
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
        <path
          d="M8 12.5l2.5 2.5L16 9"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={styles.checkDraw}
        />
      </svg>
    )
  }
  if (type === 'error') {
    return (
      <svg {...common}>
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
        <path d="M12 8v5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
        <circle cx="12" cy="16" r="1.25" fill="currentColor" />
      </svg>
    )
  }
  if (type === 'warning') {
    return (
      <svg {...common}>
        <path
          d="M12 3L2.5 20h19L12 3z"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinejoin="round"
        />
        <path d="M12 10v4" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
        <circle cx="12" cy="17" r="1.25" fill="currentColor" />
      </svg>
    )
  }
  return (
    <svg {...common}>
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
      <path d="M12 11v5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
      <circle cx="12" cy="8" r="1.25" fill="currentColor" />
    </svg>
  )
}

type Props = {
  toasts: ToastItem[]
  onDismiss: (id: number) => void
}

export function ToastList({ toasts, onDismiss }: Props) {
  if (toasts.length === 0) return null

  return (
    <div className={styles.container} aria-live="polite" aria-relevant="additions">
      {toasts.map((t) => (
        <div
          key={t.id}
          className={`${styles.toast} ${styles[`toast--${t.type}`]} ${t.exiting ? styles.toastExiting : ''}`}
          role={t.type === 'error' ? 'alert' : 'status'}
        >
          <span className={styles.accentBar} aria-hidden />
          <span className={styles.iconWrap} aria-hidden>
            <ToastIcon type={t.type} />
          </span>
          <div className={styles.body}>
            <span className={styles.title}>{t.title ?? TITLES[t.type]}</span>
            <span className={styles.message}>{t.message}</span>
          </div>
          <button
            type="button"
            className={styles.close}
            onClick={() => onDismiss(t.id)}
            aria-label="Fechar aviso"
          >
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden>
              <path
                d="M6 6l12 12M18 6L6 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
          <span
            className={styles.progress}
            style={{ animationDuration: `${t.durationMs}ms` }}
            aria-hidden
          />
        </div>
      ))}
    </div>
  )
}
